import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { UnlockCountdown } from '@/components/UnlockCountdown';
import { ArrowRight, Clock, CheckCircle2, Sparkles } from 'lucide-react';

/** "What's next" — the one thing standing between the member and their next step. */
export const NextUnlock = () => {
  const [next, setNext] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/drip/next-unlock`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((r) => setNext(r.data))
      .catch(() => {});
  }, []);

  if (!next || ['unrestricted', 'finished', 'tier_capped'].includes(next.status)) return null;

  const shell =
    'rounded-xl border border-white/10 bg-white/[0.03] p-4 block transition-colors duration-200 hover:bg-white/[0.06]';

  if (next.status === 'waiting') {
    return (
      <section data-testid="next-unlock-waiting">
        <h3 className="text-xs uppercase tracking-wider text-white/40 mb-2">Opening next</h3>
        <div className={shell}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-white/90 truncate">{next.title}</p>
              <p className="text-xs text-white/40 mt-0.5">
                {next.type === 'module' ? 'Module' : `Module ${next.module_order}`} ·{' '}
                {next.module_title}
              </p>
            </div>
            <Clock className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
          </div>
          <div className="mt-3">
            <UnlockCountdown unlockDate={next.unlock_date} testId="next-unlock-countdown" />
          </div>
        </div>
      </section>
    );
  }

  if (next.status === 'complete_module') {
    return (
      <section data-testid="next-unlock-complete-module">
        <h3 className="text-xs uppercase tracking-wider text-white/40 mb-2">Ready to close out</h3>
        <Link to={`/dashboard/modules/${next.module_id}`} className={shell}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-white/90 truncate">{next.module_title}</p>
              <p className="text-xs text-white/40 mt-0.5">
                Every folder is done — mark Module {next.module_order} complete
              </p>
            </div>
            <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" />
          </div>
        </Link>
      </section>
    );
  }

  return (
    <section data-testid="next-unlock-in-progress">
      <h3 className="text-xs uppercase tracking-wider text-white/40 mb-2">Open now</h3>
      <Link to={`/dashboard/modules/${next.module_id}`} className={shell}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-white/90 truncate">{next.title}</p>
            <p className="text-xs text-white/40 mt-0.5">
              Module {next.module_order} · {next.module_title}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <ArrowRight className="w-4 h-4 text-white/50" />
          </div>
        </div>
      </Link>
    </section>
  );
};

export default NextUnlock;
